import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ChevronRight, Circle } from 'lucide-react';
import { Link } from 'react-router-dom';
import API from '../../utils/constant';
import { ErrorPanel, LoadingState, Track } from '../../school_panel/components/ui';

export type SetupSummary = {
  grades: number; subjects: number; chapters: number; sections: number; teachers: number; students: number;
};

type Step = { key: string; title: string; body: string; to: string; done: (s: SetupSummary) => boolean; count: (s: SetupSummary) => string };

// The order a new school is set up in: what students study, how they are grouped, who teaches, who learns.
export const SETUP_STEPS: Step[] = [
  { key: 'grades', title: 'Add grades and subjects', body: 'Start from the YADA blueprint and remove what you don\'t teach, or add grades and subjects one by one.',
    to: '/school-setup/class', done: (s) => s.grades > 0 && s.subjects > 0,
    count: (s) => `${s.grades} ${s.grades === 1 ? 'grade' : 'grades'} · ${s.subjects} ${s.subjects === 1 ? 'subject' : 'subjects'}` },
  { key: 'chapters', title: 'Add chapters', body: 'Join the national chapters for each subject, or add your own.',
    to: '/school-setup/chapters', done: (s) => s.chapters > 0, count: (s) => `${s.chapters} ${s.chapters === 1 ? 'chapter' : 'chapters'}` },
  { key: 'sections', title: 'Create sections', body: 'Split each grade into sections such as 9A and 9B.',
    to: '/settings/sections', done: (s) => s.sections > 0, count: (s) => `${s.sections} ${s.sections === 1 ? 'section' : 'sections'}` },
  { key: 'teachers', title: 'Add teachers and counselors', body: 'Create their accounts and share their sign-in details, then give each teacher their grades and subjects.',
    to: '/school-setup/staff', done: (s) => s.teachers > 0, count: (s) => `${s.teachers} ${s.teachers === 1 ? 'teacher' : 'teachers'}` },
  { key: 'students', title: 'Accept students', body: 'Students join your school from the YADA app; approve their requests here.',
    to: '/students/requests', done: (s) => s.students > 0, count: (s) => `${s.students} ${s.students === 1 ? 'student' : 'students'}` },
];

export const fetchSetupSummary = () => API.get<SetupSummary>('/api/school/setup/summary/').then((r) => r.data);
export const useSetupSummary = (enabled = true) => useQuery({ queryKey: ['school-setup-summary'], queryFn: fetchSetupSummary, enabled, staleTime: 15_000 });

export const setupProgress = (s: SetupSummary) => SETUP_STEPS.filter((st) => st.done(s)).length;

export default function SetupChecklist() {
  const q = useSetupSummary();
  if (q.isLoading) return <LoadingState label="Checking your school…" />;
  if (q.isError || !q.data) return <ErrorPanel title="Couldn't check your school" body="Try again in a moment." onRetry={() => q.refetch()} />;
  const s = q.data;
  const done = setupProgress(s);
  const next = SETUP_STEPS.find((st) => !st.done(s));
  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <span className="font-mono text-[13px] font-semibold text-ink">{done} of {SETUP_STEPS.length} done</span>
        <div className="flex-1"><Track value={(done / SETUP_STEPS.length) * 100} tone="slate" label="Setup progress" /></div>
      </div>
      <ol className="divide-y divide-hairline rounded-[16px] border border-hairline">
        {SETUP_STEPS.map((st, i) => {
          const ok = st.done(s);
          const isNext = st === next;
          return (
            <li key={st.key}>
              <Link to={st.to} aria-label={`${i + 1}. ${st.title}${ok ? ' (done)' : ''}`}
                className={`flex items-start gap-3 px-4 py-3.5 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${isNext ? 'bg-note' : ''}`}>
                {ok
                  ? <CheckCircle2 size={20} aria-hidden className="mt-0.5 flex-none text-success" />
                  : <Circle size={20} aria-hidden className={`mt-0.5 flex-none ${isNext ? 'text-accent' : 'text-muted'}`} />}
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-ink">{i + 1}. {st.title}</span>
                  <span className="mt-0.5 block text-[13px] text-muted">{st.body}</span>
                  <span className="mt-1 block font-mono text-xs text-muted">{st.count(s)}</span>
                </span>
                <span className="mt-0.5 flex flex-none items-center gap-1 text-[13px] font-semibold text-primary">
                  {ok ? 'Review' : isNext ? 'Start' : 'Open'}<ChevronRight size={16} aria-hidden />
                </span>
              </Link>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
